import React, { useState } from 'react';
import styled from 'styled-components';

import { DocumentVideoUploadButton } from '@/component/FileUpload/DocumentVideoUploadButton';
import { useFileUploader } from '@/component/FileUpload/useFileUpload';
import { UploadedFile } from '@/component/FileUpload/types';
import { useVideoUploadProgress } from '@/component/customEditorV2/ui/elements/Video/hooks/useVideoUploadProgress';
import { FeedbackFileUploadUploaded } from '@/features/feedback/atoms/FeedbackFileUploadUploaded';
import { FeedbackFileUploadUploading } from '@/features/feedback/atoms/FeedbackFileUploadUploading';

const ContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const UploadButtonContainerStyled = styled.div`
  display: flex;
  align-items: center;
`;

const ErrorStyled = styled.div`
  ${({ theme }) => theme.typography.caption};
  color: ${({ theme }) => theme.colors.error.main};
`;

type FeedbackFileUploadProps = {
  spaceId?: number;
  onFileUploaded: (url: string) => void;
  onFileRemoved: () => void;
  uploadedFileUrl?: string;
};

export const FeedbackFileUpload = ({
  spaceId,
  onFileUploaded,
  onFileRemoved,
  uploadedFileUrl,
}: FeedbackFileUploadProps): JSX.Element => {
  const [fileType, setFileType] = useState<'image' | 'video'>('image');
  const [originalFileName, setOriginalFileName] = useState<string | null>(null);

  const handleImageUploadSuccess = (file: UploadedFile<'image'>) => {
    if (file.name) {
      setOriginalFileName(file.name);
    }

    onFileUploaded(file.url);
  };

  const handleVideoUploadSuccess = (file: UploadedFile<'video'>) => {
    const vimeoUrl = `https://vimeo.com/${file.vimeoId}`;
    onFileUploaded(vimeoUrl);
  };

  const handleUploadFail = () => {
    setOriginalFileName(null);
  };

  const imageUploader = useFileUploader<'image'>({
    spaceId,
    type: 'image',
    onUploadSuccess: handleImageUploadSuccess,
    onUploadFail: handleUploadFail,
  });

  const videoUploader = useFileUploader<'video'>({
    spaceId,
    type: 'video',
    onUploadSuccess: handleVideoUploadSuccess,
    onUploadFail: handleUploadFail,
  });

  const activeUploader = fileType === 'image' ? imageUploader : videoUploader;
  const isUploading = activeUploader.uploading;
  const rawUploadProgress = activeUploader.uploadingProgress;

  const transcodingInProgress = fileType === 'video' && videoUploader.uploading && videoUploader.uploadingState === 'Transcoding';
  const { current: videoProgress, prev: videoPrevProgress } = useVideoUploadProgress({
    transcodingInProgress: transcodingInProgress || false,
    vimeoProgress: videoUploader.uploadingProgress,
    maxVisualUploadingProgress: 50,
  });

  const uploadProgress = fileType === 'video' ? videoProgress : Math.round(rawUploadProgress);
  const prevUploadProgress = fileType === 'video' ? videoPrevProgress : undefined;

  const handleFileSelect = (file: File) => {
    if (uploadedFileUrl) {
      onFileRemoved();
      setOriginalFileName(null);
    }

    setOriginalFileName(file.name);

    if (file.type.startsWith('image/')) {
      setFileType('image');
      imageUploader.uploadFile(file);
    } else if (file.type.startsWith('video/')) {
      setFileType('video');
      videoUploader.uploadFile(file);
    }
  };

  if (uploadedFileUrl) {
    return (
      <FeedbackFileUploadUploaded
        uploadedFileUrl={uploadedFileUrl}
        originalFileName={originalFileName}
        fileType={fileType}
        onReplace={handleFileSelect}
        imageUploaderError={imageUploader.error}
        videoUploaderError={videoUploader.error}
      />
    );
  }

  if (isUploading) {
    return (
      <FeedbackFileUploadUploading
        uploadProgress={uploadProgress}
        prevUploadProgress={prevUploadProgress}
        uploadError={imageUploader.error || videoUploader.error}
      />
    );
  }

  return (
    <ContainerStyled>
      <UploadButtonContainerStyled>
        <DocumentVideoUploadButton
          variant="secondary"
          size="large"
          uploadFile={handleFileSelect}
          fileType={fileType}
          accept="image/*,video/*"
          type="button"
        >
          Attach file
        </DocumentVideoUploadButton>
      </UploadButtonContainerStyled>
      {(imageUploader.error || videoUploader.error) && (
        <ErrorStyled>{imageUploader.error?.message || videoUploader.error?.message}</ErrorStyled>
      )}
    </ContainerStyled>
  );
};
